/** Геометрия стен и проёмов. */
import type { Opening, WallGraph, Wall } from "@/shared/plan";
import { angleOf, dist, sub, type Pt } from "@/shared/lib/geometry";

export const wallPoints = (plan: WallGraph, wall: Wall): { a: Pt; b: Pt } | null => {
	const a = plan.nodes[wall.a];
	const b = plan.nodes[wall.b];
	if (!a || !b) return null;
	return { a, b };
};

export const wallLength = (plan: WallGraph, wall: Wall): number => {
	const p = wallPoints(plan, wall);
	return p ? dist(p.a, p.b) : 0;
};

export const wallAngle = (plan: WallGraph, wall: Wall): number => {
	const p = wallPoints(plan, wall);
	return p ? angleOf(sub(p.b, p.a)) : 0;
};

export const openingsOfWall = (plan: WallGraph, wallId: string): Opening[] => {
	return Object.values(plan.openings)
		.filter((o) => o.wallId === wallId)
		.toSorted((x, y) => x.offset - y.offset);
};

/** Прямоугольный кусок стены в системе координат стены: x вдоль стены, z по высоте. */
export interface WallPiece {
	x0: number;
	x1: number;
	z0: number;
	z1: number;
}

/**
 * Режет стену на сплошные куски с учётом проёмов:
 * простенки между проёмами, перемычки сверху и подоконные части снизу.
 */
export const wallPieces = (wall: Wall, length: number, openings: Opening[]): WallPiece[] => {
	const pieces: WallPiece[] = [];
	const items = openings
		.map((o) => {
			const half = o.width / 2;
			return {
				x0: Math.max(0, o.offset - half),
				x1: Math.min(length, o.offset + half),
				z0: Math.max(0, o.sill),
				z1: Math.min(wall.height, o.sill + o.height),
			};
		})
		.filter((o) => o.x1 > o.x0)
		.toSorted((a, b) => a.x0 - b.x0);

	let cursor = 0;
	for (const o of items) {
		if (o.x0 > cursor) pieces.push({ x0: cursor, x1: o.x0, z0: 0, z1: wall.height });
		if (o.z0 > 0) pieces.push({ x0: o.x0, x1: o.x1, z0: 0, z1: o.z0 }); // подоконная часть
		if (o.z1 < wall.height) pieces.push({ x0: o.x0, x1: o.x1, z0: o.z1, z1: wall.height }); // перемычка
		cursor = Math.max(cursor, o.x1);
	}
	if (cursor < length) pieces.push({ x0: cursor, x1: length, z0: 0, z1: wall.height });

	return pieces.filter((p) => p.x1 - p.x0 > 1 && p.z1 - p.z0 > 1);
};

/** Точка на стене по смещению вдоль неё. */
export const pointAlongWall = (a: Pt, b: Pt, offset: number): Pt => {
	const l = dist(a, b);
	if (l < 1e-6) return a;
	const t = offset / l;
	return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
};

/** Держит проём внутри стены. */
export const clampOffset = (offset: number, width: number, length: number): number => {
	const half = width / 2;
	if (length <= width) return length / 2;
	return Math.max(half, Math.min(length - half, offset));
};
